import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { toast } from 'sonner';
import { Search, Plus, Loader2, User, Phone, Mail, ChevronRight, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchAuth = async (endpoint, options = {}) => {
  const res = await fetch(`${API_URL}/api/v1${endpoint}`, {
    ...options, credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};

const ContactsPage = () => {
  const { isReadOnly } = usePlan();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    full_name: '', primary_phone: '', email: '', city: '', notes: ''
  });

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const data = await fetchAuth(`/contacts${params}`);
      setContacts(data.contacts);
      setTotal(data.total);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(loadContacts, 300);
    return () => clearTimeout(t);
  }, [loadContacts]);

  const loadDetail = async (contactId) => {
    setSelected(contactId);
    setDetailLoading(true);
    try {
      const data = await fetchAuth(`/contacts/${contactId}`);
      setDetail(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.full_name.trim() || !createForm.primary_phone.trim()) {
      toast.error('Name and phone are required');
      return;
    }
    try {
      await fetchAuth('/contacts', {
        method: 'POST',
        body: JSON.stringify(createForm),
      });
      toast.success('Contact created');
      setShowCreate(false);
      setCreateForm({ full_name: '', primary_phone: '', email: '', city: '', notes: '' });
      loadContacts();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const relTypeLabel = (t) => {
    const map = {
      blocker: 'Blocker', booker: 'Booker', purchaser: 'Purchaser',
      co_purchaser: 'Co-Purchaser', tenant: 'Tenant', co_tenant: 'Co-Tenant',
      occupying_owner: 'Occupying Owner', interested_party: 'Interested Party',
      co_booker: 'Co-Booker', authorised_representative: 'Authorised Rep',
    };
    return map[t] || t;
  };

  const relTypeColor = (t) => {
    if (t === 'purchaser' || t === 'co_purchaser') return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400';
    if (t === 'booker' || t === 'co_booker') return 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400';
    if (t === 'blocker') return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400';
    if (t === 'tenant' || t === 'co_tenant') return 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400';
    if (t === 'occupying_owner') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Contacts</h1>
          <p className="text-sm text-muted-foreground">{total} contacts in your company</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} disabled={isReadOnly} data-testid="add-contact-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Contact
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, phone, or email..."
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="contacts-search"
        />
      </div>

      <div className="flex gap-6">
        {/* Table */}
        <div className={`${selected ? 'w-3/5' : 'w-full'} transition-all`}>
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Name</TableHead>
                      <TableHead className="text-xs">Phone</TableHead>
                      <TableHead className="text-xs">Email</TableHead>
                      <TableHead className="text-xs">City</TableHead>
                      <TableHead className="w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map(c => (
                      <TableRow
                        key={c.contact_id}
                        className={`cursor-pointer ${selected === c.contact_id ? 'bg-muted' : ''}`}
                        onClick={() => loadDetail(c.contact_id)}
                        data-testid={`contact-row-${c.contact_id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary">
                              {c.full_name?.[0]?.toUpperCase() || '?'}
                            </div>
                            <span className="text-sm font-medium">{c.full_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{c.primary_phone}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{c.email || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{c.city || '-'}</TableCell>
                        <TableCell><ChevronRight className="w-3.5 h-3.5 text-muted-foreground" /></TableCell>
                      </TableRow>
                    ))}
                    {contacts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-12">
                          {search ? 'No contacts match your search' : 'No contacts yet'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail Panel */}
        {selected && detail && (
          <div className="w-2/5" data-testid="contact-detail-panel">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{detail.contact.full_name}</CardTitle>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setSelected(null); setDetail(null); }}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {detailLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="w-3.5 h-3.5" /> {detail.contact.primary_phone}
                      </div>
                      {detail.contact.email && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail className="w-3.5 h-3.5" /> {detail.contact.email}
                        </div>
                      )}
                      {detail.contact.city && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <User className="w-3.5 h-3.5" /> {detail.contact.city}{detail.contact.state ? `, ${detail.contact.state}` : ''}
                        </div>
                      )}
                    </div>

                    {/* Relationships */}
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-2">Unit Relationships ({detail.relationships.length})</p>
                      {detail.relationships.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No unit relationships</p>
                      ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {detail.relationships.map(r => (
                            <div key={r.relationship_id} className="p-2 bg-muted/30 rounded text-xs flex items-center justify-between">
                              <div>
                                <span className="font-medium">Unit {r.unit_id?.slice(-8)}</span>
                                <Badge className={`ml-2 text-[10px] ${relTypeColor(r.relationship_type)}`}>
                                  {relTypeLabel(r.relationship_type)}
                                </Badge>
                              </div>
                              <span className="text-muted-foreground">
                                {r.active_until ? 'Ended' : 'Active'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {detail.contact.notes && (
                      <div>
                        <p className="text-xs font-semibold mb-1">Notes</p>
                        <p className="text-xs text-muted-foreground">{detail.contact.notes}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Full Name *</Label>
                <Input value={createForm.full_name} onChange={e => setCreateForm(f => ({...f, full_name: e.target.value}))} placeholder="Contact name" data-testid="new-contact-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Phone *</Label>
                <Input value={createForm.primary_phone} onChange={e => setCreateForm(f => ({...f, primary_phone: e.target.value}))} placeholder="+91 98765 43210" data-testid="new-contact-phone" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Email</Label>
                <Input value={createForm.email} onChange={e => setCreateForm(f => ({...f, email: e.target.value}))} placeholder="email@example.com" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">City</Label>
                <Input value={createForm.city} onChange={e => setCreateForm(f => ({...f, city: e.target.value}))} placeholder="City" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Input value={createForm.notes} onChange={e => setCreateForm(f => ({...f, notes: e.target.value}))} placeholder="Any notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!createForm.full_name.trim() || !createForm.primary_phone.trim()} data-testid="confirm-create-contact">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsPage;
